// REST client: same-origin cookies, the CSRF header on every write, and the
// "confirm your password" step-up flow handled once, here, for every caller.
import { create } from "zustand";

let csrfToken = "";
export function setCsrf(token: string) {
  csrfToken = token;
}

/** A write that survives the page closing (fire and forget). */
export function putOnUnload(path: string, body: unknown) {
  try {
    void fetch(path, {
      method: "PUT",
      keepalive: true,
      credentials: "same-origin",
      headers: { "content-type": "application/json", "x-csrf-token": csrfToken },
      body: JSON.stringify(body),
    });
  } catch {
    /* nothing to do while the page goes away */
  }
}

export class ApiError extends Error {
  status: number;
  code?: string;
  detail: unknown;
  constructor(status: number, message: string, code?: string, detail?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

// ── step-up: a password confirmation dialog any request can ask for ─────────
interface StepUpState {
  open: boolean;
  message: string;
  resolve: ((ok: boolean) => void) | null;
  ask: (message: string) => Promise<boolean>;
  settle: (ok: boolean) => void;
}

export const useStepUp = create<StepUpState>((set, get) => ({
  open: false,
  message: "",
  resolve: null,
  ask: (message) =>
    new Promise<boolean>((resolve) => {
      get().resolve?.(false);
      set({ open: true, message, resolve });
    }),
  settle: (ok) => {
    get().resolve?.(ok);
    set({ open: false, resolve: null });
  },
}));

type Listener = () => void;
const unauthorized = new Set<Listener>();
export function onUnauthorized(fn: Listener): () => void {
  unauthorized.add(fn);
  return () => {
    unauthorized.delete(fn);
  };
}

async function request<T>(method: string, path: string, body?: unknown, retried = false): Promise<T> {
  const headers: Record<string, string> = { accept: "application/json" };
  const raw = body instanceof Blob;
  if (body !== undefined) headers["content-type"] = raw ? "application/octet-stream" : "application/json";
  if (method !== "GET" && method !== "HEAD") headers["x-csrf-token"] = csrfToken;
  // a request on a dropped mobile connection must not hang (and keep its button
  // spinning) for ever; AI answers and uploads are given longer
  const limit = path.startsWith("/api/ai/") ? 120_000 : raw ? 180_000 : 30_000;
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), limit);
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers,
      credentials: "same-origin",
      body: body === undefined ? undefined : raw ? body : JSON.stringify(body),
      signal: ctl.signal,
    });
  } catch {
    clearTimeout(timer);
    if (ctl.signal.aborted) throw new ApiError(0, "The server took too long to answer. Check your connection and try again.", "timeout");
    throw new ApiError(0, "Cannot reach the server. Check your connection.");
  }
  clearTimeout(timer);
  if (res.ok) {
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }
  let data: Record<string, unknown> = {};
  try {
    data = await res.json();
  } catch {
    /* not JSON */
  }
  const message = String(data.message ?? data.detail ?? res.statusText ?? "Request failed");
  if (res.status === 401 && !path.startsWith("/api/auth/")) {
    unauthorized.forEach((fn) => fn());
  }
  if (res.status === 403 && data.code === "step_up_required" && !retried) {
    const ok = await useStepUp.getState().ask(message);
    if (ok) return request<T>(method, path, body, true);
    throw new ApiError(403, "Cancelled", "step_up_cancelled");
  }
  throw new ApiError(res.status, message, data.code as string | undefined, data);
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body ?? {}),
  del: <T>(path: string) => request<T>("DELETE", path),
  /** A file as the raw request body. */
  upload: <T>(path: string, file: Blob) => request<T>("POST", path, file),
};

/** Save JSON as a file through the browser's download. */
export function downloadJson(data: unknown, filename: string) {
  const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function qs(params: Record<string, string | number | boolean | null | undefined>) {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "") u.set(k, String(v));
  const s = u.toString();
  return s ? `?${s}` : "";
}

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const d = e.detail as { findings?: { message: string }[]; errors?: string[] } | undefined;
    const extra = d?.errors?.length ? `: ${d.errors.join("; ")}` : "";
    return e.message + extra;
  }
  if (e instanceof Error) return e.message;
  return String(e);
}
